/**
 * Colunas dinâmicas pivot (defeito, condição final) em parque + chamados.
 */

import {
  FONTE_CHAMADOS_TECNICOS,
  FONTE_PARQUE_LOCADO,
} from '../constants/relatoriosFontes';

export const PIVOT_COLUMN_CONFIG = {
  defeito: {
    mainId: 'defeito',
    metaKey: 'defeitosDisponiveis',
    selecionadosKey: 'defeitoSelecionados',
  },
  condicaoFinal: {
    mainId: 'condicaoFinal',
    metaKey: 'condicoesDisponiveis',
    selecionadosKey: 'condicaoSelecionadas',
  },
  tipoOs: {
    mainId: 'tipoOs',
    metaKey: 'tiposOsDisponiveis',
    selecionadosKey: 'tipoOsSelecionados',
  },
};

export const PIVOT_COLUMN_IDS = Object.keys(PIVOT_COLUMN_CONFIG);

export function pivotToSlug(label) {
  const s = String(label ?? '').trim();
  if (!s) return '_';
  return encodeURIComponent(s).replace(/%/g, '_');
}

export function pivotFromSlug(slug) {
  if (!slug || slug === '_') return '';
  try {
    return decodeURIComponent(String(slug).replace(/_/g, '%'));
  } catch {
    return String(slug);
  }
}

export function pivotColumnKey(columnId, slug) {
  return `${FONTE_CHAMADOS_TECNICOS}:${columnId}:${slug}`;
}

export function pivotMainColumnKey(columnId) {
  return `${FONTE_CHAMADOS_TECNICOS}:${columnId}`;
}

export function parsePivotKey(key) {
  if (!key || typeof key !== 'string') return null;
  for (const columnId of PIVOT_COLUMN_IDS) {
    const prefix = `${FONTE_CHAMADOS_TECNICOS}:${columnId}:`;
    if (!key.startsWith(prefix)) continue;
    const slug = key.slice(prefix.length);
    if (!slug) return null;
    return { columnId, slug, label: pivotFromSlug(slug) };
  }
  return null;
}

export function isPivotKey(key) {
  return Boolean(parsePivotKey(key));
}

export function normalizePivotSelecionados(arr) {
  if (!Array.isArray(arr)) return null;
  if (arr.length === 0) return [];
  const slugs = arr.map((s) => String(s).trim()).filter(Boolean);
  return slugs.length > 0 ? slugs : [];
}

function getPivotSelecionadosRaw(ref, selecionadosKey) {
  if (!ref || ref[selecionadosKey] === undefined) return null;
  if (!Array.isArray(ref[selecionadosKey])) return null;
  if (ref[selecionadosKey].length === 0) return [];
  return ref[selecionadosKey].map((s) => String(s).trim()).filter(Boolean);
}

export function expandDisplayColumnKeys(orderedKeys, meta, columnRefs, primaryFonte) {
  let keys = [...(orderedKeys || [])];
  if (primaryFonte === FONTE_CHAMADOS_TECNICOS) {
    return keys;
  }
  const refByMain = Object.fromEntries(
    (columnRefs || []).map((r) => [r.id, r])
  );

  for (const columnId of PIVOT_COLUMN_IDS) {
    const cfg = PIVOT_COLUMN_CONFIG[columnId];
    const mainKey = pivotMainColumnKey(columnId);
    if (!keys.includes(mainKey)) continue;

    const ref = refByMain[columnId];
    const selected = getPivotSelecionadosRaw(ref, cfg.selecionadosKey);
    const pool = Array.isArray(meta?.[cfg.metaKey]) ? meta[cfg.metaKey] : [];
    let filtered = pool;
    if (Array.isArray(selected)) {
      filtered = selected.length === 0 ? [] : pool.filter((d) => selected.includes(d.slug));
    }

    const next = [];
    for (const key of keys) {
      if (key !== mainKey) {
        next.push(key);
        continue;
      }
      if (filtered.length > 0) {
        for (const d of filtered) {
          next.push(pivotColumnKey(columnId, d.slug));
        }
      } else if (primaryFonte === FONTE_PARQUE_LOCADO && !Array.isArray(selected)) {
        next.push(mainKey);
      }
    }
    keys = next;
  }
  return keys;
}

export function buildPivotColumnLabels(meta) {
  const labels = {};
  for (const columnId of PIVOT_COLUMN_IDS) {
    const cfg = PIVOT_COLUMN_CONFIG[columnId];
    for (const d of meta?.[cfg.metaKey] || []) {
      if (d?.slug) {
        labels[pivotColumnKey(columnId, d.slug)] = d.label || pivotFromSlug(d.slug);
      }
    }
  }
  return labels;
}

export function collapseDisplayKeysToStored(keys) {
  const out = [];
  const inserted = new Set();
  for (const key of keys || []) {
    const pivot = parsePivotKey(key);
    if (pivot) {
      if (!inserted.has(pivot.columnId)) {
        out.push(pivotMainColumnKey(pivot.columnId));
        inserted.add(pivot.columnId);
      }
      continue;
    }
    out.push(key);
  }
  return out;
}

/** @deprecated use pivotToSlug */
export const defeitoToSlug = pivotToSlug;
/** @deprecated use parsePivotKey */
export const parseDefeitoPivotKey = (key) => {
  const p = parsePivotKey(key);
  return p?.columnId === 'defeito' ? p : null;
};
